import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../supabase';
import { FiUsers, FiUserPlus, FiAlertCircle, FiCheckCircle, FiSettings, FiInfo, FiMail, FiClock } from 'react-icons/fi';
import AdminDashboardNav from '../comps/AdminDashboardNav';
import '../styles/AdminManagement.css';

const AdminManagement = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [auditLog, setAuditLog] = useState([]);
  const [auditLoading, setAuditLoading] = useState(true);
  
  // Form states
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [newAdminUsername, setNewAdminUsername] = useState('');
  const [newAdminRole, setNewAdminRole] = useState('admin');
  const [submitting, setSubmitting] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

  const getAuthToken = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token || null;
  };

  // Master is a DB role now (backend/routes/admins.js enforces this exact
  // check too) - no email-based fallback anymore, that was a leftover from
  // before the role column existed and pointed at an account that isn't
  // even an admin anymore.
  const isMasterAdmin = (email, role) => role === 'master';

  // Send password reset email
  const handleSendPasswordReset = async (email) => {
    try {
      const token = await getAuthToken();

      const response = await fetch(`${API_URL}/api/admins/send-password-reset`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send password reset');
      }

      setSuccess(`E-post for tilbakestilling av passord sendt til ${email}`);
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error('Error sending password reset:', err);
      setError(`Kunne ikke sende e-post for tilbakestilling av passord: ${err.message}`);
      setTimeout(() => setError(''), 5000);
    }
  };

  // Fetch admins
  const fetchAdmins = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = await getAuthToken();
      if (!token) {
        setError('Ikke autentisert');
        return;
      }

      const response = await fetch(`${API_URL}/api/admins`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Kunne ikke hente admins');
      }

      const data = await response.json();
      setAdmins(data.admins);
    } catch (err) {
      console.error('Error fetching admins:', err);
      setError('Kunne ikke laste adminliste');
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  // Fetch audit log
  const fetchAuditLog = useCallback(async () => {
    try {
      setAuditLoading(true);
      const token = await getAuthToken();
      if (!token) return;

      const response = await fetch(`${API_URL}/api/admins/audit-log`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Kunne ikke hente historikk');

      const data = await response.json();
      setAuditLog(data.entries);
    } catch (err) {
      console.error('Error fetching audit log:', err);
    } finally {
      setAuditLoading(false);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchAdmins();
    fetchAuditLog();
  }, [fetchAdmins, fetchAuditLog]);

  const AUDIT_ACTION_LABELS = {
    admin_created: 'Admin opprettet',
    admin_self_registered: 'Admin selvregistrert',
    admin_revoked: 'Admintilgang tilbakekalt',
    admin_role_changed: 'Rolle endret',
    admin_2fa_toggled: '2FA endret',
    password_reset_sent: 'Tilbakestilling av passord sendt',
    password_set_via_otp: 'Passord satt opp'
  };

  // Add new admin
  const handleAddAdmin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const token = await getAuthToken();

      const response = await fetch(`${API_URL}/api/admins`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: newAdminEmail,
          username: newAdminUsername,
          role: newAdminRole
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to add admin');
      }

      // Show appropriate success message
      if (data.admin?.isNewUser) {
        if (data.admin?.passwordEmailSent) {
          setSuccess(
            `✅ Ny admin "${newAdminUsername}" opprettet! ` +
            `En e-post for å sette passord er sendt til ${newAdminEmail}.`
          );
        } else {
          setSuccess(
            `✅ Ny admin "${newAdminUsername}" opprettet, men e-posten kunne ikke sendes. ` +
            `Klikk på "Tilbakestill"-knappen for å prøve på nytt.`
          );
        }
      } else {
        setSuccess(`✅ Admin "${newAdminUsername}" lagt til!`);
      }
      
      setNewAdminEmail('');
      setNewAdminUsername('');
      setNewAdminRole('admin');
      
      // Refresh admin list
      await fetchAdmins();
      fetchAuditLog();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);

    } catch (err) {
      console.error('Error adding admin:', err);
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Revoke admin access
  const handleRevokeAdmin = async (username, email, role) => {
    // Prevent revoking master admin
    if (isMasterAdmin(email, role)) {
      setError('Kan ikke tilbakekalle master-admin-tilgang. Denne kontoen er beskyttet.');
      setTimeout(() => setError(''), 3000);
      return;
    }

    if (!window.confirm(`Er du sikker på at du vil tilbakekalle admintilgang for "${username}"?`)) {
      return;
    }

    try {
      setError('');
      setSuccess('');
      
      const token = await getAuthToken();

      // The route matches by email, not display name - this used to send
      // `username` here, which only ever worked if someone's display name
      // happened to equal their email.
      const response = await fetch(`${API_URL}/api/admins/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to revoke admin');
      }

      setSuccess(`Admintilgang tilbakekalt for "${username}"`);

      // Refresh admin list
      await fetchAdmins();
      fetchAuditLog();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);

    } catch (err) {
      console.error('Error revoking admin:', err);
      setError(err.message);
    }
  };

  // Change an existing admin's role (admin <-> spectator only - master is
  // excluded server-side too, can only be set/removed manually in Supabase).
  const handleChangeRole = async (email, newRole) => {
    try {
      setError('');
      setSuccess('');

      const token = await getAuthToken();
      const response = await fetch(`${API_URL}/api/admins/${encodeURIComponent(email)}/role`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ role: newRole }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to change role');
      }

      setSuccess(`Rolle for "${email}" endret til ${newRole === 'admin' ? 'Admin' : 'Spectator'}`);
      await fetchAdmins();
      fetchAuditLog();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error changing role:', err);
      setError(err.message);
    }
  };

  // Toggle an admin's monthly 2FA requirement on/off - a convenience
  // exception, not a role/access change, so no master-protection needed here.
  const handleToggle2FA = async (email, disabled) => {
    try {
      setError('');
      setSuccess('');

      const token = await getAuthToken();
      const response = await fetch(`${API_URL}/api/admins/${encodeURIComponent(email)}/2fa`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ disabled }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to toggle 2FA');
      }

      setSuccess(`2FA ${disabled ? 'deaktivert' : 'aktivert'} for "${email}"`);
      await fetchAdmins();
      fetchAuditLog();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error toggling 2FA:', err);
      setError(err.message);
    }
  };

  return (
    <>
      <AdminDashboardNav />
      <div className="admin-management-container">
        <div className="admin-management-header">
          <h1><FiUsers style={{ marginRight: '10px', verticalAlign: 'middle' }} /> Adminstrering</h1>
          <p>Administrer adminbrukere og tillatelser</p>
        </div>

      {error && (
        <div className="alert alert-error">
          <FiAlertCircle className="alert-icon" />
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <FiCheckCircle className="alert-icon" />
          <span style={{ whiteSpace: 'pre-line' }}>{success}</span>
        </div>
      )}

      {/* Add New Admin Section */}
      <div className="card add-admin-card">
        <h2><FiUserPlus style={{ marginRight: '8px', verticalAlign: 'middle' }} /> Legg til ny admin</h2>
        
        <div className="info-message" style={{ 
          background: 'white', 
          borderRadius: '12px', 
          padding: '16px 20px', 
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
          transition: 'all 0.2s ease'
        }}>
          <FiInfo style={{ color: '#010f3c', marginTop: '2px', flexShrink: 0, fontSize: '18px' }} />
          <div style={{ fontSize: '14px', color: '#64748b', lineHeight: '1.6' }}>
            <strong style={{ color: '#010f3c' }}>Nye brukere:</strong> Hvis e-postadressen ikke finnes, opprettes en ny bruker automatisk og en e-post for å sette passord sendes til dem.
            Kom den ikke frem? Bruk "Tilbakestill"-knappen for å sende den på nytt.
          </div>
        </div>

        <form onSubmit={handleAddAdmin} className="add-admin-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email">E-postadresse</label>
              <input
                type="email"
                id="email"
                value={newAdminEmail}
                onChange={(e) => setNewAdminEmail(e.target.value)}
                placeholder="admin@playworld.com"
                required
                disabled={submitting}
              />
              <small>Bruker opprettes automatisk hvis de ikke finnes</small>
            </div>

            <div className="form-group">
              <label htmlFor="username">Brukernavn</label>
              <input
                type="text"
                id="username"
                value={newAdminUsername}
                onChange={(e) => setNewAdminUsername(e.target.value)}
                placeholder="john"
                required
                disabled={submitting}
                pattern="[a-zA-Z0-9_\-]+"
                title="Kun bokstaver, tall, bindestreker og understreker tillatt"
              />
              <small>Visningsnavn for denne adminen</small>
            </div>

            <div className="form-group">
              <label htmlFor="role">Rolle</label>
              <select
                id="role"
                value={newAdminRole}
                onChange={(e) => setNewAdminRole(e.target.value)}
                disabled={submitting}
                style={{
                  padding: '10px 12px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'Poppins, sans-serif',
                  background: '#f8f9fa',
                  color: '#010f3c',
                  cursor: 'pointer'
                }}
              >
                <option value="admin">Admin - Full tilgang</option>
                <option value="spectator">Tilskuer - Kun visning</option>
              </select>
              <small>Master-rollen finnes, men settes kun manuelt i Supabase</small>
            </div>
          </div>

          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={submitting}
          >
            {submitting ? (
              <>
                <span className="spinner"></span>
                Legger til admin...
              </>
            ) : (
              <>
                <FiUserPlus style={{ fontSize: '18px' }} />
                Legg til admin
              </>
            )}
          </button>
        </form>
      </div>

      {/* Admin List Section */}
      <div className="card admins-list-card">
        <h2><FiSettings style={{ marginRight: '8px', verticalAlign: 'middle' }} /> Nåværende admins</h2>
        
        {loading ? (
          <div className="loading-container">
            <div className="spinner-large"></div>
            <p>Laster admins...</p>
          </div>
        ) : admins.length === 0 ? (
          <div className="empty-state">
            <p>Ingen adminbrukere funnet</p>
          </div>
        ) : (
          <div className="admins-table">
            <table>
              <thead>
                <tr>
                  <th>Brukernavn</th>
                  <th>E-post</th>
                  <th>Rolle</th>
                  <th>Status</th>
                  <th>2FA</th>
                  <th>Opprettet</th>
                  <th>Handlinger</th>
                </tr>
              </thead>
              <tbody>
                {admins.map((admin) => {
                  const isMaster = isMasterAdmin(admin.email, admin.role);
                  const roleConfig = {
                    master: { label: 'Master', color: '#f59e0b', bgColor: '#fef3c7' },
                    admin: { label: 'Admin', color: '#010f3c', bgColor: '#e0e7ff' },
                    spectator: { label: 'Spectator', color: '#64748b', bgColor: '#f1f5f9' }
                  };
                  const role = roleConfig[admin.role] || roleConfig.admin;
                  
                  return (
                  <tr key={admin.email} className={admin.status !== 'active' ? 'revoked' : ''}>
                    <td>
                      <strong>{admin.username}</strong>
                    </td>
                    <td>{admin.email}</td>
                    <td>
                      {isMaster ? (
                        <span style={{
                          background: role.bgColor,
                          color: role.color,
                          fontSize: '12px',
                          padding: '4px 10px',
                          borderRadius: '6px',
                          fontWeight: '600',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          {role.label}
                        </span>
                      ) : (
                        <select
                          value={admin.role === 'master' ? 'admin' : admin.role}
                          onChange={(e) => handleChangeRole(admin.email, e.target.value)}
                          disabled={admin.status !== 'active'}
                          style={{
                            fontSize: '12px',
                            padding: '4px 8px',
                            borderRadius: '6px',
                            fontWeight: '600',
                            border: `1px solid ${role.color}33`,
                            background: role.bgColor,
                            color: role.color,
                            cursor: admin.status !== 'active' ? 'not-allowed' : 'pointer',
                          }}
                        >
                          <option value="admin">Admin</option>
                          <option value="spectator">Tilskuer</option>
                        </select>
                      )}
                    </td>
                    <td>
                      <span className={`status-badge status-${admin.status}`}>
                        <span className={`status-dot ${admin.status === 'active' ? 'active' : 'revoked'}`}></span>
                        {admin.status}
                      </span>
                    </td>
                    <td>
                      <label
                        title={admin.twofaDisabled ? 'Deaktivert - klikk for a aktivere' : 'Aktiv - klikk for a deaktivere'}
                        style={{ display: 'inline-flex', alignItems: 'center', cursor: admin.status !== 'active' ? 'not-allowed' : 'pointer' }}
                      >
                        <input
                          type="checkbox"
                          checked={!admin.twofaDisabled}
                          disabled={admin.status !== 'active'}
                          onChange={(e) => handleToggle2FA(admin.email, !e.target.checked)}
                          style={{ width: 16, height: 16, cursor: admin.status !== 'active' ? 'not-allowed' : 'pointer' }}
                        />
                      </label>
                    </td>
                    <td>
                      {admin.createdAt
                        ? new Date(admin.createdAt).toLocaleDateString('no-NO', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })
                        : 'N/A'}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button
                          onClick={() => handleSendPasswordReset(admin.email)}
                          className="btn btn-secondary btn-sm"
                          style={{
                            background: '#6366f1',
                            color: 'white',
                            border: 'none',
                            padding: '6px 12px',
                            borderRadius: '6px',
                            fontSize: '13px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            cursor: 'pointer'
                          }}
                          title="Send e-post for tilbakestilling av passord"
                        >
                          <FiMail size={14} />
                          Tilbakestill
                        </button>
                        {admin.status === 'active' ? (
                          isMaster ? (
                            <span style={{ color: '#94a3b8', fontSize: '13px', fontStyle: 'italic' }}>
                              Beskyttet
                            </span>
                          ) : (
                            <button
                              onClick={() => handleRevokeAdmin(admin.username, admin.email, admin.role)}
                              className="btn btn-danger btn-sm"
                            >
                              Tilbakekall
                            </button>
                          )
                        ) : (
                          <span className="text-muted">Tilbakekalt</span>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Audit Log Section */}
      <div className="card">
        <h2><FiClock style={{ marginRight: '8px', verticalAlign: 'middle' }} /> Aktivitetslogg</h2>

        {auditLoading ? (
          <div className="loading-container">
            <div className="spinner-large"></div>
            <p>Laster historikk...</p>
          </div>
        ) : auditLog.length === 0 ? (
          <div className="empty-state">
            <p>Ingen registrert aktivitet ennå</p>
          </div>
        ) : (
          <div className="admins-table">
            <table>
              <thead>
                <tr>
                  <th>Tidspunkt</th>
                  <th>Handling</th>
                  <th>Utført av</th>
                  <th>Mål</th>
                </tr>
              </thead>
              <tbody>
                {auditLog.map((entry) => (
                  <tr key={entry.id}>
                    <td>
                      {entry.timestamp
                        ? new Date(entry.timestamp).toLocaleString('no-NO', {
                            year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                          })
                        : 'N/A'}
                    </td>
                    <td>{AUDIT_ACTION_LABELS[entry.action] || entry.action}</td>
                    <td>{entry.actor}</td>
                    <td>{entry.target || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info Card */}
      <div className="card info-card">
        <h3><FiInfo style={{ marginRight: '8px', verticalAlign: 'middle' }} /> Viktig informasjon</h3>
        <ul>
          <li><strong>Master:</strong> Beskyttet rolle med fulle rettigheter som ikke kan tilbakekalles</li>
          <li><strong>Admin:</strong> Full tilgang til alle admin-funksjoner og administrasjon</li>
          <li><strong>Tilskuer:</strong> Kun lesetilgang, kan ikke endre data - selvregistrerte admins starter alltid som Tilskuer</li>
          <li>Bruk rolle-nedtrekket i tabellen for å endre en admin mellom Admin og Tilskuer</li>
          <li>Nye admins opprettes automatisk med autentiseringskontoer</li>
          <li>Du kan ikke tilbakekalle din egen admintilgang</li>
          <li>Tilbakekalte admins merkes i systemet, men beholder kontoen sin</li>
          <li>Admintillatelser verifiseres på alle beskyttede ruter</li>
        </ul>
      </div>
    </div>
    </>
  );
};

export default AdminManagement;
